/**
 * C6.5 + C6.6 — Painel de proveniência de injeções do Cortex.
 *
 * Mostra as últimas N execuções dos nós que de fato afetam a resposta:
 *   • ToM        — pipeline_tom_stage (telemetry_events)
 *   • Affect     — pipeline_affect_stage (telemetry_events)
 *   • Creative   — pipeline_creative_stage (telemetry_events)   [C5.6]
 *   • Banter     — banter_detections (tabela própria)            [C5.5]
 *
 * Sem placebo: cada linha referencia `message_id` real (ou null quando
 * o nó pulou antes de ter contexto), métricas reais e indica claramente
 * quando o nó NÃO injetou (skipped) e por quê.
 *
 * Org-escopado via RLS.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "@/hooks/useSkillOS";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Brain,
  Heart,
  Lightbulb,
  Smile,
  CheckCircle2,
  MinusCircle,
  AlertCircle,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

type Row = {
  id: number | string;
  created_at: string;
  event_type: string;
  group_id: string | null;
  payload: Record<string, unknown>;
};

const TOM_TYPES = ["tom_injected", "tom_skipped"];
const AFFECT_TYPES = ["affect_injected", "affect_skipped"];
const CREATIVE_TYPES = ["creative_injected", "creative_skipped"];

function useTelemetryRows(types: string[], orgId: string | undefined, limit = 25) {
  return useQuery({
    queryKey: ["cortex-injections", types.join(","), orgId, limit],
    enabled: !!orgId,
    refetchInterval: 20_000,
    queryFn: async (): Promise<Row[]> => {
      const { data, error } = await supabase
        .from("telemetry_events")
        .select("id, created_at, event_type, group_id, payload")
        .eq("org_id", orgId!)
        .in("event_type", types)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });
}

function useBanterRows(orgId: string | undefined, limit = 25) {
  return useQuery({
    queryKey: ["cortex-banter-detections", orgId, limit],
    enabled: !!orgId,
    refetchInterval: 20_000,
    queryFn: async (): Promise<Row[]> => {
      const { data, error } = await supabase
        .from("banter_detections")
        .select("id, created_at, group_id, message_id, banter_score, signals, downgraded_intent_from, downgraded_intent_to")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []).map((r: any) => ({
        id: r.id,
        created_at: r.created_at,
        group_id: r.group_id,
        // Tipo virtual: "downgraded" quando intent foi rebaixado, "evaluated" caso contrário
        event_type: r.downgraded_intent_to ? "banter_downgraded" : "banter_evaluated",
        payload: {
          banter_score: r.banter_score,
          signals: r.signals,
          intent_from: r.downgraded_intent_from,
          intent_to: r.downgraded_intent_to,
          message_id: r.message_id,
        },
      })) as Row[];
    },
  });
}

function StatusIcon({ injected }: { injected: boolean }) {
  if (injected)
    return <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />;
  return <MinusCircle className="w-4 h-4 text-muted-foreground shrink-0" />;
}

function tomDescribe(p: Record<string, unknown>): string {
  const conf = typeof p.confidence === "number" ? p.confidence : null;
  const obs = typeof p.observation_count === "number" ? p.observation_count : null;
  const reason = typeof p.reason === "string" ? p.reason : null;
  if (reason) return `pulado: ${reason}${conf != null ? ` (conf ${conf.toFixed(2)})` : ""}`;
  return `conf ${conf?.toFixed(2) ?? "?"} · ${obs ?? "?"} obs · +${p.block_chars ?? "?"} chars`;
}

function affectDescribe(p: Record<string, unknown>): string {
  const v = typeof p.valence === "number" ? p.valence : null;
  const a = typeof p.arousal === "number" ? p.arousal : null;
  const reason = typeof p.reason === "string" ? p.reason : null;
  if (reason && reason !== "ok")
    return `pulado: ${reason}${v != null ? ` (V ${v.toFixed(2)} / A ${a?.toFixed(2) ?? "?"})` : ""}`;
  return `V ${v?.toFixed(2) ?? "?"} · A ${a?.toFixed(2) ?? "?"} · +${p.block_chars ?? "?"} chars`;
}

function creativeDescribe(p: Record<string, unknown>): string {
  const reason = typeof p.reason === "string" ? p.reason : null;
  const ideas = typeof p.ideas_count === "number" ? p.ideas_count : null;
  if (reason) return `pulado: ${reason}`;
  return `${ideas ?? "?"} ideias · +${p.block_chars ?? "?"} chars`;
}

function banterDescribe(p: Record<string, unknown>): string {
  const score = typeof p.banter_score === "number" ? p.banter_score : null;
  const from = typeof p.intent_from === "string" ? p.intent_from : null;
  const to = typeof p.intent_to === "string" ? p.intent_to : null;
  const sig = (p.signals ?? {}) as Record<string, unknown>;
  const ed = typeof sig.emoji_density === "number" ? sig.emoji_density : null;
  const ls = typeof sig.laughter_streak === "number" ? sig.laughter_streak : null;
  if (from && to) {
    return `score ${score?.toFixed(2) ?? "?"} → downgrade ${from} → ${to}`;
  }
  return `score ${score?.toFixed(2) ?? "?"} · emoji ${ed?.toFixed(2) ?? "?"} · risadas ${ls?.toFixed(2) ?? "?"}`;
}

function RowItem({
  row,
  describe,
  injectedType,
  injectedLabel,
  skippedLabel,
}: {
  row: Row;
  describe: (p: Record<string, unknown>) => string;
  injectedType: string;
  injectedLabel?: string;
  skippedLabel?: string;
}) {
  const injected = row.event_type === injectedType;
  const msgId = (row.payload?.message_id as string | null) ?? null;
  return (
    <div className="flex items-start gap-2 py-2 border-b border-border/50 last:border-0 text-xs">
      <StatusIcon injected={injected} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge
            variant={injected ? "default" : "outline"}
            className="text-[10px] px-1.5 py-0"
          >
            {injected ? (injectedLabel ?? "INJETADO") : (skippedLabel ?? "PULADO")}
          </Badge>
          <span className="text-muted-foreground">
            {formatDistanceToNow(new Date(row.created_at), { addSuffix: true, locale: ptBR })}
          </span>
        </div>
        <div className="mt-0.5 text-foreground">{describe(row.payload)}</div>
        {msgId && (
          <div className="mt-0.5 text-[10px] text-muted-foreground font-mono truncate">
            msg: {msgId}
          </div>
        )}
      </div>
    </div>
  );
}

function StageCard({
  title,
  icon: Icon,
  rows,
  loading,
  injectedType,
  describe,
  injectedLabel,
  skippedLabel,
  ratioLabel,
}: {
  title: string;
  icon: typeof Brain;
  rows: Row[] | undefined;
  loading: boolean;
  injectedType: string;
  describe: (p: Record<string, unknown>) => string;
  injectedLabel?: string;
  skippedLabel?: string;
  ratioLabel?: string;
}) {
  const injectedCount = rows?.filter((r) => r.event_type === injectedType).length ?? 0;
  const total = rows?.length ?? 0;
  const ratio = total > 0 ? Math.round((injectedCount / total) * 100) : 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Icon className="w-4 h-4" />
            {title}
          </span>
          <Badge variant="secondary" className="text-[10px]">
            {injectedCount}/{total} {ratioLabel ?? `(${ratio}%)`}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="max-h-[420px] overflow-y-auto">
        {loading ? (
          <div className="text-xs text-muted-foreground py-4 text-center">Carregando…</div>
        ) : !rows || rows.length === 0 ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-4">
            <AlertCircle className="w-4 h-4" />
            Sem registros nas últimas execuções.
          </div>
        ) : (
          rows.map((r) => (
            <RowItem
              key={r.id}
              row={r}
              injectedType={injectedType}
              describe={describe}
              injectedLabel={injectedLabel}
              skippedLabel={skippedLabel}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

export default function CortexInjectionsProvenance() {
  const { data: orgId } = useCurrentOrgId();
  const tom = useTelemetryRows(TOM_TYPES, orgId);
  const affect = useTelemetryRows(AFFECT_TYPES, orgId);
  const creative = useTelemetryRows(CREATIVE_TYPES, orgId);
  const banter = useBanterRows(orgId);

  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground">
        Proveniência real das últimas injeções dos nós do Cortex no pipeline
        (ToM, Affect, Creative, Banter). Cada linha vem da tabela
        correspondente com <code className="text-[10px]">message_id</code> verificável — sem placebo.
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <StageCard
          title="Theory of Mind — últimas injeções"
          icon={Brain}
          rows={tom.data}
          loading={tom.isLoading}
          injectedType="tom_injected"
          describe={tomDescribe}
        />
        <StageCard
          title="Affect — últimas injeções"
          icon={Heart}
          rows={affect.data}
          loading={affect.isLoading}
          injectedType="affect_injected"
          describe={affectDescribe}
        />
        <StageCard
          title="Creative (DMN-divergente) — últimas injeções"
          icon={Lightbulb}
          rows={creative.data}
          loading={creative.isLoading}
          injectedType="creative_injected"
          describe={creativeDescribe}
        />
        <StageCard
          title="Banter — detecções e downgrades"
          icon={Smile}
          rows={banter.data}
          loading={banter.isLoading}
          injectedType="banter_downgraded"
          describe={banterDescribe}
          injectedLabel="DOWNGRADE"
          skippedLabel="AVALIADO"
        />
      </div>
    </div>
  );
}
